
import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Shield, Ruler, ClipboardCheck, Users } from 'lucide-react';

const PEOVettingProcess = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-green-50">
      <Helmet>
        <title>Our PEO Vetting Process | The PEO App</title>
        <meta name="description" content="Learn how The PEO App ensures quality by vetting all PEO partners through a rigorous screening process, continuous monitoring, and client-focused matching." />
        <link rel="canonical" href="https://thepeoapp.com/peo-vetting-process" />
      </Helmet>
      
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Rigorous PEO Vetting Process
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              We hold our PEO partners to the highest industry standards, ensuring businesses receive reliable, 
              compliant, and financially stable solutions.
            </p>
          </motion.div>

          {/* Process Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* Quality Standards Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center gap-4 mb-6">
                <Shield className="h-8 w-8 text-primary" />
                <h2 className="text-2xl font-semibold text-gray-900">Quality Standards</h2>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="h-2 w-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <p className="text-gray-700">ESAC accreditation verification</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-2 w-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <p className="text-gray-700">IRS certification standards compliance</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-2 w-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <p className="text-gray-700">State-specific licensing requirements</p>
                </li>
              </ul>
            </motion.div>

            {/* Screening Process Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center gap-4 mb-6">
                <Ruler className="h-8 w-8 text-primary" />
                <h2 className="text-2xl font-semibold text-gray-900">Screening Process</h2>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="h-2 w-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <p className="text-gray-700">Financial stability assessment</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-2 w-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <p className="text-gray-700">Service capability evaluation</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-2 w-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <p className="text-gray-700">Client satisfaction analysis</p>
                </li>
              </ul>
            </motion.div>

            {/* Continuous Monitoring Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center gap-4 mb-6">
                <ClipboardCheck className="h-8 w-8 text-primary" />
                <h2 className="text-2xl font-semibold text-gray-900">Continuous Monitoring</h2>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="h-2 w-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <p className="text-gray-700">Regular performance reviews</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-2 w-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <p className="text-gray-700">Client feedback integration</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-2 w-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <p className="text-gray-700">Compliance verification</p>
                </li>
              </ul>
            </motion.div>

            {/* Client Matching Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center gap-4 mb-6">
                <Users className="h-8 w-8 text-primary" />
                <h2 className="text-2xl font-semibold text-gray-900">Client Matching</h2>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="h-2 w-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <p className="text-gray-700">Industry expertise alignment</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-2 w-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <p className="text-gray-700">Geographic coverage verification</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-2 w-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <p className="text-gray-700">Service scope compatibility</p>
                </li>
              </ul>
            </motion.div>
          </div>

          {/* Bottom CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="text-center bg-white rounded-2xl p-12 shadow-lg"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Ready to Work with Vetted PEO Partners?
            </h2>
            <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
              Our thorough vetting process ensures you're connected with PEOs that meet your specific needs
              and maintain the highest standards of service.
            </p>
            <a
              href="/application"
              className="inline-block bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-4 rounded-full transition-colors"
            >
              Start Your Search Now
            </a>
          </motion.div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PEOVettingProcess;
